/**
 * Search – find other athletes by name. Links to their profile.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { listUsers } from '../../domain/usecases/profile';
import { DISCIPLINE_LABELS } from '../../domain/entities/User';
import Avatar from '../../shared/ui/Avatar.jsx';
import Badge from '../../shared/ui/Badge.jsx';
import Card from '../../shared/ui/Card.jsx';
import Body from '../../shared/ui/Body.jsx';
import Display from '../../shared/ui/Display.jsx';
import EmptyState from '../../shared/ui/EmptyState.jsx';
import Input from '../../shared/ui/Input.jsx';
import Skeleton from '../../shared/ui/Skeleton.jsx';
import styles from './SearchPage.module.css';

export function SearchPage() {
  const { userId: myId } = useAuth();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listUsers(200).then((list) => {
      setUsers(list.filter((u) => u.id !== myId));
      setLoading(false);
    });
  }, [myId]);

  const q = query.trim().toLowerCase();
  const filtered = q
    ? users.filter((u) => (u.displayName || '').toLowerCase().includes(q))
    : users;

  return (
    <div className={styles.page}>
      <Display as="h1" size="l" style={{ marginBottom: '0.25rem' }}>Search</Display>
      <Body tone="secondary" style={{ marginBottom: '1rem' }}>Find athletes by name</Body>

      <Input
        label="SEARCH"
        placeholder="Find an athlete"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        type="search"
        aria-label="Search athletes"
        autoFocus
      />

      {loading ? (
        <div className={styles.skeletonList}>
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} height={64} radius="var(--radius-md)" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon="search"
          title="No results"
          body={query.trim() ? 'Try a different handle.' : 'Type a name to search.'}
        />
      ) : (
        <ul className={styles.list}>
          {filtered.map((u) => (
            <li key={u.id}>
              <Link to={`/profile/${u.id}`} className={styles.link}>
                <Card padding="sm" className={styles.row}>
                  <Avatar
                    src={u.avatarUrl || undefined}
                    initials={u.displayName?.charAt(0)?.toUpperCase() || '?'}
                    size="md"
                  />
                  <div className={styles.info}>
                    <Body as="span" style={{ fontWeight: 600, display: 'block' }}>
                      {u.displayName || 'Athlete'}
                    </Body>
                    {(u.nationality || u.discipline) && (
                      <Body as="span" tone="secondary" style={{ fontSize: 13 }}>
                        {[u.nationality, u.discipline ? DISCIPLINE_LABELS[u.discipline] : null]
                          .filter(Boolean)
                          .join(' · ')}
                      </Body>
                    )}
                  </div>
                  {u.verified && <Badge variant="gold">Verified</Badge>}
                  <span className={styles.arrow}>→</span>
                </Card>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
